import DOMPurify from 'dompurify';

const INLINE_TAGS = ['a', 'br', 'b', 'strong', 'u', 'i', 'em'];
const DOCUMENT_TAGS = [
  'a',
  'br',
  'p',
  'hr',
  'strong',
  'em',
  'u',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'ul',
  'ol',
  'li',
  'blockquote',
  'table',
  'thead',
  'tbody',
  'tfoot',
  'tr',
  'th',
  'td',
  'img',
];

// Keep document markup aligned with RichTextSanitizer in Rails.
const LINK_ATTRIBUTES = ['href', 'title', 'target', 'rel', 'data-internal-page-id'];
const DOCUMENT_ATTRIBUTES = [
  ...LINK_ATTRIBUTES,
  'colspan',
  'rowspan',
  'scope',
  'src',
  'alt',
  'width',
  'height',
  'loading',
  'class',
  'id',
  'style',
];
const TEXT_ALIGN_VALUES = new Set(['left', 'center', 'right', 'justify']);
const SAFE_SCHEMES = new Set(['http:', 'https:', 'mailto:', 'tel:', 'sms:']);

export type RichTextProfile = 'inline' | 'document';

// Browser-only: SSR callers must render escaped text or server-sanitized HTML.
export function sanitizeRichText(value: string, profile: RichTextProfile): string {
  const content = DOMPurify.sanitize(value, {
    ALLOWED_TAGS: profile === 'document' ? DOCUMENT_TAGS : INLINE_TAGS,
    ALLOWED_ATTR: profile === 'document' ? DOCUMENT_ATTRIBUTES : LINK_ATTRIBUTES,
    ALLOW_DATA_ATTR: false,
    ALLOW_ARIA_ATTR: false,
    // Allow relative URLs as well as the supported explicit schemes.
    ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel|sms):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
    RETURN_DOM: true,
  });
  if (!(content instanceof HTMLElement)) return '';

  // DOMPurify permits data URLs on some media tags; enforce our URL policy too.
  content.querySelectorAll('[href], [src]').forEach((node) => {
    for (const attribute of ['href', 'src']) {
      const value = node.getAttribute(attribute);
      if (value === null) continue;
      try {
        if (
          !value.trim() ||
          !SAFE_SCHEMES.has(new URL(value, 'https://preview.invalid').protocol)
        ) {
          node.removeAttribute(attribute);
        }
      } catch {
        node.removeAttribute(attribute);
      }
    }
  });
  content.querySelectorAll('[style]').forEach((node) => {
    const alignment = node
      .getAttribute('style')
      ?.split(';')
      .map((declaration) => declaration.split(':').map((part) => part.trim()))
      .find(([property, value]) => property === 'text-align' && TEXT_ALIGN_VALUES.has(value));
    if (alignment) {
      node.setAttribute('style', `text-align: ${alignment[1]};`);
    } else {
      node.removeAttribute('style');
    }
  });
  content.querySelectorAll('[data-internal-page-id]').forEach((node) => {
    if (node.localName !== 'a') node.removeAttribute('data-internal-page-id');
  });
  content.querySelectorAll('a').forEach((link) => {
    if (link.getAttribute('target') === '_blank') {
      link.setAttribute('rel', 'noopener noreferrer');
    } else {
      link.removeAttribute('target');
      link.removeAttribute('rel');
    }
  });
  return content.innerHTML;
}
